import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import { InsStatus } from "parog-web/cnam/types";
import type MedicModel from "parog-web/models/medic";
import type MedicalHistoryChangedJournalEvent from "parog-web/models/medical-history-changed-journal-event";
import type Patient from "parog-web/models/patient";
import { getDateFromString } from "parog-web/utils/time";
import {
  correspondenceTable,
  dialysisCareLocationTable,
  dialysisTypeTable,
  followUpReasonTable
} from "./correspondance-tables";

interface CsvPatientRow {
  firstName: string;
  lastName: string;
  birthName?: string;
  birthday: string;
  gender?: string;
  email?: string;
  phone: string;
  address?: string;
  postcode?: string;
  city?: string;
  moreAddressInformation?: string;
  ippNumber?: string;
  insuredSocialSecurityNumber?: string;
  medics?: string;
  // Health Centre
  healthCentre: string;
  // Medical History
  transplantDate?: string;
  nephropatie?: string;
  donorType?: string;
  mrcStage?: string;
  importedFollowUpReason?: string;
  dialysisType?: string;
  dialysisCareLocation?: string;
}

const mandatoryValues = [
  "firstName",
  "lastName",
  "birthday",
  "phone",
  "healthCentre"
];

export class PatientWithJEs {
  @tracked declare error?: string;
  @tracked isSaving = false;
  @tracked declare isSuccess?: boolean;

  constructor(
    public patient: Patient,
    public medicalHistory: MedicalHistoryChangedJournalEvent,
    public invalidValues: string[]
  ) {}
}

export function getPatientStructured(store: Store, csvRow: CsvPatientRow) {
  const { firstName, birthName } = csvRow;
  const birthday = getDateFromString(csvRow.birthday);
  const gender = csvRow.gender == "F" ? "female" : "male";
  const lastName = csvRow.lastName || csvRow.birthName;
  const { email } = csvRow;
  const { address, postcode, city, moreAddressInformation } = csvRow;
  const phone = getPhoneStr(csvRow.phone);
  const { ippNumber, insuredSocialSecurityNumber } = csvRow;
  const insStatus = InsStatus.Provisional;

  const healthCentre = store.peekRecord("health-centre", csvRow.healthCentre);

  const medics: MedicModel[] = [];
  const medicsIds = csvRow.medics ? csvRow.medics?.split(",") : undefined;
  medicsIds?.forEach(medicId => {
    const medic = store.peekRecord("medic", medicId) as MedicModel;
    if (medic) {
      medics.push(medic);
    }
  });

  const hcp = store.createRecord("health-centre-cohort", {
    healthCentre,
    ippNumber: ippNumber,
    role: "configurator"
  });

  const importedFollowUpReason = csvRow.importedFollowUpReason
    ? followUpReasonTable.get(csvRow.importedFollowUpReason)
    : undefined;

  return {
    firstName,
    lastName,
    birthName,
    birthday,
    gender,
    address,
    postcode,
    city,
    moreAddressInformation,
    email,
    phone,
    insuredSocialSecurityNumber,
    insStatus,
    medics,
    healthCentreCohorts: [hcp],
    importedFollowUpReason
  };
}

export function createPatientAndMedicalHistory(store: Store, row: any) {
  transformKeys(row);

  const patient = store.createRecord(
    "patient",
    getPatientStructured(store, row)
  );

  const medicalHistory = store.createRecord(
    "medical-history-changed-journal-event",
    getMedicalHistoryStructured(patient, row)
  );

  return new PatientWithJEs(patient, medicalHistory, getInvalidValues(patient));
}

export function getMedicalHistoryStructured(
  patient: Patient,
  csvRow: CsvPatientRow
) {
  const occurredAt = new Date();
  const transplantDate = csvRow.transplantDate
    ? getDateFromString(csvRow.transplantDate)
    : undefined;
  const { nephropatie, donorType } = csvRow;
  const mrcStage = getMrcStage(csvRow.mrcStage);

  const dialysisType = csvRow.dialysisType
    ? dialysisTypeTable.get(csvRow.dialysisType)
    : undefined;
  const dialysisCareLocation = csvRow.dialysisCareLocation
    ? dialysisCareLocationTable.get(csvRow.dialysisCareLocation)
    : undefined;

  return {
    patient,
    occurredAt,
    transplantDate,
    nephropatie,
    donorType,
    mrcStage,
    dialysisType,
    dialysisCareLocation
  };
}

export function transformKeys(row: any) {
  correspondenceTable.forEach(function (patientKey, sheetKey) {
    row[patientKey] = row[sheetKey];
    delete row[sheetKey];
  });
  return row;
}

/**
 * An imported patient is valid if he provides the mandatory values
 */
export function getInvalidValues(patient: Patient): string[] {
  // Set the "validity" of the patient. A patient is not "valid" if it does not have the mandatory values.
  const invalidValues: string[] = [];
  for (const valueKey of mandatoryValues) {
    if (!patient[valueKey]) {
      // Add the french name of the value
      correspondenceTable.forEach(function (patientKey, frenchName) {
        if (patientKey == valueKey) {
          invalidValues.push(frenchName);
        }
      });
    }
  }
  return invalidValues;
}

function getPhoneStr(phoneNumber: string): string {
  if (phoneNumber) {
    const str = phoneNumber.toString().replace(/ /g, "");
    return str.length == 9 ? "0" + str : str;
  }
  return phoneNumber;
}

function getMrcStage(mrc: string | undefined): string | null {
  if (mrc) {
    return "stage-" + mrc;
  }
  return null;
}
